/**
 * `/c/<eventId>` — arriving at an event, from the code on the wall (GH-772, GH-782).
 *
 * ## The one place identity comes before content
 *
 * Everywhere else in this product a donor may do the thing first and be a stranger while they do
 * it: the founding promise is a first gift with no account (CLAUDE.md), and `/e/<eventId>` keeps
 * it — its Give button is not gated on a session, a name, or a check-in. This page is the owner's
 * single, scoped exception: *arriving at an event* asks who you are, because presence is a claim
 * about a person and there is nobody to record without one.
 *
 * The exception is scoped to arriving and **not** to giving, which is why the skip below is not
 * decoration. A donor who will not sign in reaches the event page and gives from it, and the only
 * thing they lose is the poll, the raffle, the question and the wall — the things the server
 * itself refuses to a donor who is not checked in (`requireCheckedIn`). Making the skip loud
 * enough to find and quiet enough not to be the obvious answer is the whole design of this
 * screen; pushed either way it becomes a dark pattern, in one direction against the donor and in
 * the other against the organizers who printed the code.
 *
 * ## What it says it does, and what it does not
 *
 * *"Checking in tells the organizers you are here. It sends no location."* — the sentence iOS's
 * `CheckInView` already carries, repeated verbatim rather than paraphrased. The request this page
 * makes carries an event id and the donor's own bearer token and nothing else; there is no
 * geolocation call anywhere in this file, and a screen with "check in" written on it invites the
 * assumption strongly enough that the denial has to be on the screen and not only in a comment.
 *
 * ## No second way to sign in
 *
 * The phone-and-code pair, and Apple and Google beside it (GH-865), are the mechanisms this bundle
 * has (`donor-session.ts`, `provider-sign-in.ts`), and the door is literally the sheet's own
 * (`SignInChoices` in `SignInSteps.tsx`). This page adds no credential, no cookie, and
 * no storage of its own — `rememberToken` is the same one line `DonorPanel` runs, so a donor
 * remembered here is remembered on the home page and vice versa.
 *
 * ## The four things a person can arrive into
 *
 *  - **Remembered already.** The common case for a returning donor, and the arrival is posted
 *    without a tap. It is never posted *silently*: the screen states what happened, and — where
 *    the check-in moved from another room — which room it moved from.
 *  - **Already checked in here.** Said plainly and passed straight through. No second POST: the
 *    session already names this event, and re-posting would be a write whose only effect would be
 *    to move `checkedInAt`.
 *  - **A stranger.** Phone, code, then the arrival, in that order.
 *  - **Somewhere check-in cannot happen.** A missing event, an event that has not started, one
 *    that has ended, or a server that would not answer. Each of the four says which it is, and
 *    every one of them still offers the way to the event page — the check-in is what failed, not
 *    the evening.
 *
 * ## A hall scan may move a check-in; a page view may not
 *
 * `DonorPanel` distinguishes an explicit tap (`joinEventId`, which overwrites) from passive page
 * presence (`hereEventId`, which fills an empty check-in only, so that reopening a sheet cannot
 * undo a switch somebody made). Scanning the code on this wall, in this room, is unambiguously
 * the first of those: it is a deliberate act performed in a place. So this page overwrites, and
 * then says so.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  DonorSessionResponse,
  DoorPrizeEntryWire,
  EventPageResponse,
} from "@givehere/contracts";
import { ApiError, fetchEventPage } from "./api.js";
import {
  confirmDonorSignIn,
  DonorApiError,
  donorCheckIn,
  fetchDonorSession,
  rememberToken,
  startDonorSignIn,
  storedToken,
} from "./donor-session.js";
import { EventOpensAtEvent, isEventAccessRequired } from "./EventAccess.js";
import { EventJoinCard, useEventJoin } from "./EventJoin.js";
import { Explain } from "./Explain.js";
import { InviteCodeEntry, InviteRequiredStep, useSignUpAccess } from "./InviteAccess.js";
import { eventAccessFor } from "./event-access.js";
import { appEventLink, isAppleMobile, openInAppHref } from "./open-in-app.js";
import { OrgMark } from "./OrgMark.js";
import { useProviderSignIn } from "./provider-sign-in.js";
import { isSignUpRefusal, type DoorAttempt } from "./sign-up-access.js";
import { CodeStep, SignInChoices } from "./SignInSteps.js";
import { isPhoneVerificationRequired, VerifyMobileStep } from "./VerifyMobile.js";

/** The one status `POST /v1/donor/check-ins` accepts; every other status is a different screen. */
const CHECKABLE_STATUS = "live";

/** Nothing left to arrive at. Separated from "not yet" because the two need opposite sentences. */
const FINISHED_STATUSES = new Set(["ended", "archived", "suspended"]);

/** `access`: membership is on and this event is not open to this visitor (GH-871b). */
type LoadFailure = "not_found" | "unavailable" | "access";

/**
 * Where the donor is in the arrival, which is not the same thing as where they are in sign-in.
 *
 * `unknown` is the state before the stored token has been checked — deliberately distinct from
 * `stranger`, because rendering the phone form during that gap would show a sign-in prompt to a
 * remembered donor for one paint and then snatch it away.
 */
type Arrival =
  | { kind: "unknown" }
  | { kind: "stranger" }
  | { kind: "arriving" }
  | {
      kind: "here";
      movedFrom: string | null;
      already: boolean;
      /** GH-883: the door prizes this arrival entered; empty when none, or already here. */
      doorPrizes: readonly DoorPrizeEntryWire[];
    }
  /** GH-883: the server wants a verified mobile first (only where it can send a code). */
  | { kind: "verifyPhone" }
  | { kind: "refused"; message: string };

/** The sign-in journey, owned here because what follows the code differs from the sheet's. */
type SignIn =
  | { kind: "phone" }
  | { kind: "code"; phone: string; verificationId: string; hint: string | null }
  /** GH-871b: a new person without a usable invite. */
  | { kind: "invite"; attempt: DoorAttempt; refusal: DonorApiError };

export function CheckInPage({
  eventId,
  joinToken = null,
}: {
  eventId: string;
  /**
   * The big screen's auto-invite (`?join=`, GH-864e). Scanning it and signing in here makes the
   * donor a member; it never changes what check-in or giving do.
   */
  joinToken?: string | null;
}) {
  const eventJoin = useEventJoin(joinToken);
  const [page, setPage] = useState<EventPageResponse | null>(null);
  const [loadFailure, setLoadFailure] = useState<LoadFailure | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [session, setSession] = useState<DonorSessionResponse | null>(null);
  const [arrival, setArrival] = useState<Arrival>({ kind: "unknown" });
  const [signIn, setSignIn] = useState<SignIn>({ kind: "phone" });
  const [busy, setBusy] = useState(false);
  const [signInFailure, setSignInFailure] = useState<string | null>(null);
  /** Bumped when the at-event location check opens this event: read the page again. */
  const [reload, setReload] = useState(0);

  /*
    One automatic arrival per visit.

    Without the latch the effect below would re-post every time the session state it just set
    caused a render, and `StrictMode`'s double-invoke would make that two writes on the first
    paint of every visit. The check-in is an upsert, so the damage would be a moved `checkedInAt`
    rather than a duplicate row — but a timestamp is what an organizer reads to know when the
    room filled, and moving it for nothing is not free. The retry button calls `arrive` directly,
    which is why the latch does not have to be cleared to try again.
  */
  const attempted = useRef(false);

  /* ------------------------------------------------------------------- event */

  useEffect(() => {
    let live = true;
    setLoadFailure(null);
    fetchEventPage(eventId)
      .then((value) => {
        if (!live) return;
        /*
          A general fund is not a happening (migration 0026): there is no room, no door and
          nobody to tell you are at it. Arriving at one is a mis-scan or a stale link, and the
          honest destination is the organization itself. `replace` rather than `assign` so the
          back button points at wherever the donor really came from.
        */
        if (value.event.isGeneralFund) {
          window.location.replace(`/o/${encodeURIComponent(value.organization.id)}`);
          return;
        }
        setPage(value);
      })
      .catch((caught: unknown) => {
        if (!live) return;
        // The public event read answers for an event in any status, so `not_found` here means
        // there is genuinely no such event — a mistyped or misprinted code, not one that has
        // not started. That distinction is the whole reason the two messages differ.
        setLoadFailure(
          isEventAccessRequired(caught)
            ? "access"
            : caught instanceof ApiError && caught.code === "not_found"
              ? "not_found"
              : "unavailable",
        );
      });
    return () => {
      live = false;
    };
  }, [eventId, reload]);

  /* ---------------------------------------------------------------- identity */

  useEffect(() => {
    const stored = storedToken();
    if (!stored) {
      setArrival({ kind: "stranger" });
      return;
    }
    let live = true;
    fetchDonorSession(stored).then(
      (value) => {
        if (!live) return;
        setToken(stored);
        setSession(value);
      },
      () => {
        // An expired or revoked token makes this person a stranger again, which is ordinary and
        // not an error worth a red box: they sign in and carry on. The stale token is left in
        // storage for `DonorPanel`'s own sign-out path to clear, because clearing somebody's
        // remembered device on a failed network read would be the more destructive guess.
        if (live) setArrival({ kind: "stranger" });
      },
    );
    return () => {
      live = false;
    };
  }, []);

  /* ----------------------------------------------------------------- arrival */

  /**
   * Post the arrival, or recognise that there is nothing to post.
   *
   * Takes its token and session as arguments rather than reading state, because the sign-in path
   * calls it in the same tick it receives them and React state would still be the previous
   * render's. `movedFrom` is computed *before* the write, since the response is the new truth and
   * the old room is gone from it.
   */
  const arrive = useCallback(
    (activeToken: string, current: DonorSessionResponse) => {
      if (current.checkIn?.eventId === eventId) {
        setArrival({ kind: "here", movedFrom: null, already: true, doorPrizes: [] });
        return;
      }
      const movedFrom = current.checkIn?.eventName ?? null;
      setArrival({ kind: "arriving" });
      donorCheckIn(activeToken, eventId).then(
        (updated) => {
          setSession(updated);
          setArrival({
            kind: "here",
            movedFrom,
            already: false,
            doorPrizes: updated.doorPrizes ?? [],
          });
        },
        (caught: unknown) =>
          // GH-883: a mobile verified by text first, then the same arrival again.
          isPhoneVerificationRequired(caught)
            ? setArrival({ kind: "verifyPhone" })
            : setArrival({
                kind: "refused",
                // The server's own words where it gave any — it is the side that knows whether
                // the event stopped being live between the load and the tap.
                message:
                  caught instanceof DonorApiError ? caught.message : "Could not check you in.",
              }),
      );
    },
    [eventId],
  );

  /**
   * A session from any door — the code, Apple or Google (GH-865) — and then the arrival.
   *
   * Straight into the arrival, in this tick, rather than letting the effect below notice the new
   * state. The effect would work — but it would render the sign-in form for one paint between the
   * session arriving and the arrival starting, which reads as the sign-in having failed. The latch
   * is set first so the effect does not then post a second time. The device is already
   * remembered by whichever door issued the session.
   */
  const arriveSignedIn = (issued: DonorSessionResponse) => {
    setToken(issued.token);
    setSession(issued);
    attempted.current = true;
    arrive(issued.token, issued);
    // Signing in from the big screen's code is the auto-invite (owner amendment 3). A gate that is
    // off answers 404 and the card simply stays hidden. A new person's sign-in carried the token
    // and joined with the account (GH-871b); `settle` reads that instead of joining twice.
    if (joinToken) void eventJoin.settle(issued);
  };

  /*
    The invite code and the big screen's join token, on every door (GH-871b). Signing up from this
    page's own form is "signing up from that flow", so the scanned join token is what lets a new
    person in.
  */
  const access = useSignUpAccess({ joinToken, enabled: arrival.kind === "stranger" });
  const refused = (attempt: DoorAttempt, refusal: DonorApiError) => {
    setSignInFailure(null);
    setSignIn({ kind: "invite", attempt, refusal });
  };

  /*
    Apple and Google beside the phone (GH-865), offered exactly where the profile sheet offers
    them: the server's options for this origin, on any browser (GH-866). Fetched only for a
    stranger — a remembered donor never sees the form.
  */
  const providers = useProviderSignIn({
    enabled: arrival.kind === "stranger",
    setBusy,
    setError: setSignInFailure,
    signUpAccess: access.fields,
    onSignUpRefused: refused,
  });

  const status = page?.event.status ?? null;

  useEffect(() => {
    if (attempted.current) return;
    if (!token || !session) return;
    // Waiting for the event before posting, so that an event that is not live shows its own
    // sentence instead of a refusal from a request that was never going to be accepted.
    if (status !== CHECKABLE_STATUS) return;
    attempted.current = true;
    arrive(token, session);
  }, [token, session, status, arrive]);

  /* ------------------------------------------------------------- early exits */

  if (loadFailure === "access") {
    // GH-871b: "Opens at the event" — Give, and the at-event location check. The auto-invite card
    // stays above it: the big screen's join token still lets the scanner join.
    return (
      <EventOpensAtEvent eventId={eventId} onOpened={() => setReload((count) => count + 1)}>
        <EventJoinCard
          joinToken={joinToken}
          token={token}
          join={eventJoin}
          onSession={(issued) => {
            setToken(issued.token);
            setSession(issued);
          }}
        />
      </EventOpensAtEvent>
    );
  }

  if (loadFailure !== null) {
    return (
      <main className="card card--landing">
        <p className="notice notice--error" role="alert">
          {loadFailure === "not_found"
            ? "This event could not be found. Check the code and try again."
            : /* Not "check your connection" and not "the server is down": this bundle cannot
                 tell the two apart, and naming the wrong one sends somebody off to fix
                 something that is not broken. */
              "This page could not be loaded. Try again, or search the directory."}
        </p>
        <a className="landing__quiet" href="/">
          Back to the directory
        </a>
      </main>
    );
  }

  if (page === null) {
    return (
      <main className="card card--landing">
        <p className="notice">Loading…</p>
      </main>
    );
  }

  /* ------------------------------------------------------------------ render */

  const { organization, event } = page;
  const eventHref = `/e/${encodeURIComponent(eventId)}`;
  /*
    GH-873: on an iPhone, the way into the app with this event (`open-in-app.ts`). Before the
    check-in it carries the join token, so the app's own sign-in can join; after it, only the
    access token — this page has already settled the join.
  */
  const appLink = isAppleMobile()
    ? appEventLink({
        origin: window.location.origin,
        eventId,
        accessToken: eventAccessFor(eventId),
        joinToken: arrival.kind === "here" ? null : joinToken,
      })
    : null;
  const running = event.status === CHECKABLE_STATUS;
  const finished = FINISHED_STATUSES.has(event.status);

  return (
    <main className="card card--landing eventpage">
      <a className="link eventpage__back" href="/">
        ‹ Directory
      </a>

      {/* Identification, not navigation. The event page's version of this row is a link to the
          organization with an ⓘ beside it; here the row exists to answer "is this the right
          room" before somebody types a phone number into a page they found on a wall. A second
          destination on an identity gate is a way out that is not the honest one — and the
          honest one, the skip, is already on this screen. */}
      <header className="eventpage__org">
        <OrgMark
          displayName={organization.displayName}
          logoUrl={organization.logoUrl}
          className="eventpage__mark"
          size={44}
        />
        <span className="eventpage__ident">
          <span className="eventpage__orgname" dir="auto">
            {organization.displayName}
          </span>
          {/* The server's own verification wording, rendered verbatim: it is a statement about a
              registry record, never a badge this page composes. */}
          <span className="eventpage__meta">
            {[organization.verificationLabel, organization.locality].filter(Boolean).join(" · ")}
          </span>
        </span>
      </header>

      <h1 className="landing__event eventpage__name" dir="auto">
        {event.name}
      </h1>

      <EventJoinCard
        joinToken={joinToken}
        token={token}
        join={eventJoin}
        signInBelow={running && arrival.kind === "stranger"}
        // The way on to the event after the join (GH-864g) — only where this page is not already
        // showing one, so the screen never has two: not before or after the event ("Go to the
        // event"), and not once checked in ("Continue to the event").
        continueHref={running && arrival.kind !== "here" ? eventHref : undefined}
        onSession={(issued) => {
          setToken(issued.token);
          setSession(issued);
        }}
      />

      {!running && (
        /*
          Two sentences for two different futures, and neither of them is "check-in failed". The
          server would refuse a check-in at either, so offering the form would be offering a
          control that cannot work — and the event page is still worth reaching in both cases:
          before the doors it is where the goal and the tickets are, after them it is where the
          final total is.
        */
        <>
          <p className="notice" role="status">
            {finished
              ? "This event has ended, so there is no longer anywhere to check in."
              : "Check-in opens when this event starts."}
          </p>
          <a className="pay landing__give" href={eventHref}>
            Go to the event
          </a>
        </>
      )}

      {running && arrival.kind === "unknown" && <p className="notice">Loading…</p>}

      {running && arrival.kind === "arriving" && (
        // Almost never read: a remembered donor's arrival is one request and this is gone before
        // the sentence is finished. It exists so the gap is a state rather than a blank card.
        <p className="notice" role="status">
          Checking you in…
        </p>
      )}

      {running && arrival.kind === "here" && (
        <>
          <p className="notice" role="status">
            {arrival.already
              ? `You are already checked in at ${event.name}.`
              : `You are checked in at ${event.name}.`}
          </p>
          {arrival.movedFrom !== null && (
            /*
              Said out loud because it is a change the donor did not ask for in words. Scanning a
              code in a room is an explicit arrival and it moves the check-in — but a donor who
              was at another event this evening deserves to learn that from the screen rather than
              from an organizer's attendance list.
            */
            <p className="landing__welcome">
              You were checked in at {arrival.movedFrom}. You are here now.
            </p>
          )}
          {arrival.doorPrizes.map((entry) => (
            <DoorPrizeLine key={entry.promptId} entry={entry} />
          ))}
          {/*
            A link and not an automatic redirect. A timed jump would race the sentence above and
            leave the donor on the event page with no idea what had been recorded about them,
            which is the "never silently" the owner asked for; it would also trap the back button,
            since returning to this page would re-run the arrival and throw them forward again.
          */}
          <a className="pay landing__give" href={eventHref}>
            Continue to the event
          </a>
        </>
      )}

      {running && arrival.kind === "verifyPhone" && token !== null && session !== null && (
        <VerifyMobileStep
          token={token}
          headingId="checkin-step"
          // The number is on the profile now: post the same arrival again.
          onVerified={() => arrive(token, session)}
        />
      )}

      {running && arrival.kind === "refused" && (
        <>
          <p className="notice notice--error" role="alert">
            {arrival.message}
          </p>
          <button
            type="button"
            className="pay"
            onClick={() => token && session && arrive(token, session)}
          >
            Try again
          </button>
        </>
      )}

      {running && arrival.kind === "stranger" && (
        <>
          {signIn.kind === "phone" ? (
            <SignInChoices
              busy={busy}
              headingId="checkin-step"
              heading="Sign in to check in"
              lede="Confirm it is you, and we remember you on this device after that."
              appleOffered={providers.appleOffered}
              onApple={() => void providers.signInWithApple(arriveSignedIn)}
              googleOffered={providers.googleOffered}
              onGoogle={() => void providers.signInWithGoogle(arriveSignedIn)}
              footer={<InviteCodeEntry access={access} />}
              onPhone={(phone) => {
                setSignInFailure(null);
                setBusy(true);
                startDonorSignIn(phone)
                  .then((started) =>
                    setSignIn({
                      kind: "code",
                      phone,
                      verificationId: started.verificationId,
                      hint: started.developmentCode,
                    }),
                  )
                  .catch((caught: unknown) =>
                    setSignInFailure(
                      caught instanceof DonorApiError ? caught.message : "Could not send a code.",
                    ),
                  )
                  .finally(() => setBusy(false));
              }}
            />
          ) : signIn.kind === "invite" ? (
            <InviteRequiredStep
              attempt={signIn.attempt}
              refusal={signIn.refusal}
              access={access}
              headingId="checkin-step"
              // A page about an event never draws a place in line (DEC-051): the waitlist is a link.
              waitlist="link"
              onIssued={arriveSignedIn}
              onRestart={() => setSignIn({ kind: "phone" })}
            />
          ) : (
            <CodeStep
              hint={signIn.hint}
              busy={busy}
              headingId="checkin-step"
              onSubmit={(code) => {
                setSignInFailure(null);
                setBusy(true);
                const attempt = {
                  door: "phone" as const,
                  verificationId: signIn.verificationId,
                  code,
                  phone: signIn.phone,
                };
                confirmDonorSignIn({
                  verificationId: signIn.verificationId,
                  code,
                  phone: signIn.phone,
                  ...access.fields(),
                })
                  .then((issued) => {
                    rememberToken(issued.token);
                    arriveSignedIn(issued);
                  })
                  .catch((caught: unknown) => {
                    if (isSignUpRefusal(caught)) {
                      refused(attempt, caught);
                      return;
                    }
                    setSignInFailure(
                      caught instanceof DonorApiError
                        ? caught.message
                        : "That code is not correct.",
                    );
                  })
                  .finally(() => setBusy(false));
              }}
            />
          )}
          {signInFailure !== null && (
            <p className="notice notice--error" role="alert">
              {signInFailure}
            </p>
          )}
        </>
      )}

      {/* The denial, on the screen and not only in a comment. Kept for every state of a live
          event including the one after arriving, because "what did I just tell them" is a
          question a person asks *after* they have done it at least as often as before. */}
      {running && (
        <Explain
          className="landing__welcome"
          label="Checking in sends no location"
          title="What checking in does"
        >
          <p>Checking in tells the organizers you are here. It sends no location.</p>
          <p>
            It enters you in any door prize the organizers are running, and lets you vote in polls
            and ask questions. You can give without checking in.
          </p>
        </Explain>
      )}

      {/*
        The skip, and it is deliberately a plain link.

        Present because the alternative is a sign-in wall in front of a gift, which this product
        does not have anywhere else and must not acquire here — the owner scoped required check-in
        to arriving at an event, never to giving, and CLAUDE.md's account-free first gift is not
        negotiable. Quiet because taking part is what this screen is offering and burying its own
        offer under an escape hatch would be a strange thing to do. It says what it is rather than
        "no thanks", so nobody has to work out what they are declining, and it is a full-height
        tap target rather than fine print, so declining is as easy to hit as accepting.
      */}
      {appLink !== null && (
        <a className="link landing__quiet" href={openInAppHref(appLink)}>
          Open in the app
        </a>
      )}

      {running && arrival.kind !== "here" && (
        <a className="link landing__quiet" href={eventHref}>
          Go to the event without checking in
        </a>
      )}
    </main>
  );
}

/**
 * One door prize the arrival entered (GH-883), said in a sentence: the ticket number to listen
 * for, or why there is none. Text, not a badge — the number is what the desk matches.
 */
function DoorPrizeLine({ entry }: { entry: DoorPrizeEntryWire }) {
  const name = entry.prize ?? entry.title;
  return (
    <p className="landing__welcome" dir="auto">
      {entry.status === "entered" && entry.entryNumber !== null
        ? `You're entered in the door prize (${name}) — entry #${entry.entryNumber}.`
        : `This mobile number is already entered in the door prize (${name}).`}
    </p>
  );
}
